import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Loader2 } from 'lucide-react';

const JSONPersistenceTest = () => {
  const [resumeText, setResumeText] = useState<string>('');
  const [coverLetterText, setCoverLetterText] = useState<string>('');
  const [resumeJsonData, setResumeJsonData] = useState<any>(null);
  const [coverLetterJsonData, setCoverLetterJsonData] = useState<any>(null);
  const [resumeDocumentId, setResumeDocumentId] = useState<string | null>(null);
  const [coverLetterDocumentId, setCoverLetterDocumentId] = useState<string | null>(null);
  const [isGeneratingJson, setIsGeneratingJson] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  // Load existing data on mount - EXACT same pattern as TailoredDocumentDetail
  useEffect(() => {
    loadExistingDocuments();
  }, []);

  const loadExistingDocuments = async () => {
    try {
      setLoading(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Load existing test documents
      const { data: documents, error } = await supabase
        .from('tailored_documents')
        .select('*')
        .eq('user_id', user.id)
        .eq('tailoring_session_id', 'TEST_SESSION')
        .in('document_type', ['resume', 'cover_letter']);

      if (error) {
        console.error('Error loading documents:', error);
        return;
      }

      console.log('🔍 DEBUG: Loading existing documents and JSON data...');
      for (const doc of documents || []) {
        // Load content
        if (doc.document_type === 'resume') {
          setResumeText(doc.content || '');
          setResumeDocumentId(doc.id);
        } else if (doc.document_type === 'cover_letter') {
          setCoverLetterText(doc.content || '');
          setCoverLetterDocumentId(doc.id);
        }

        // Load JSON data using EXACT same method as TailoredDocumentDetail
        const jsonData = await loadFromResumeJsonTable(doc.id);
        console.log(`📄 DEBUG: Document ${doc.id} (${doc.document_type}) JSON data:`, {
          hasJsonData: !!jsonData,
          dataSize: jsonData ? JSON.stringify(jsonData).length : 0
        });

        if (jsonData) {
          if (doc.document_type === 'resume') {
            setResumeJsonData(jsonData);
          } else if (doc.document_type === 'cover_letter') {
            setCoverLetterJsonData(jsonData);
          }
        }
      }
    } catch (error) {
      console.error('Error loading existing documents:', error);
    } finally {
      setLoading(false);
    }
  };

  // EXACT same function as TailoredDocumentDetail
  const saveToResumeJsonTable = async (documentId: string, jsonData: any, documentType: string, title: string) => {
    try {
      const { data: user } = await supabase.auth.getUser();
      if (!user.user) return;

      const { error } = await supabase
        .from('resumejs')
        .upsert({
          user_id: user.user.id,
          document_id: documentId,
          document_type: documentType,
          json_data: jsonData,
          title: title
        }, {
          onConflict: 'user_id,document_id'
        });

      if (error) {
        console.error('Error saving to resumejs table:', error);
      } else {
        console.log('Successfully saved JSON data to independent resumejs table');
      }
    } catch (error) {
      console.error('Error in saveToResumeJsonTable:', error);
    }
  };

  // EXACT same function as TailoredDocumentDetail
  const loadFromResumeJsonTable = async (documentId: string) => {
    try {
      const { data: user } = await supabase.auth.getUser();
      if (!user.user) return null;

      const { data, error } = await supabase
        .from('resumejs')
        .select('json_data')
        .eq('user_id', user.user.id)
        .eq('document_id', documentId)
        .single();

      if (error) {
        console.log('No data found in resumejs table for document:', documentId);
        return null;
      }

      return data?.json_data || null;
    } catch (error) {
      console.error('Error in loadFromResumeJsonTable:', error);
      return null;
    }
  };

  // Create or update document in tailored_documents table
  const createOrUpdateDocument = async (content: string, documentType: 'resume' | 'cover_letter') => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;

      const existingDocId = documentType === 'resume' ? resumeDocumentId : coverLetterDocumentId;
      
      if (existingDocId) {
        // Update existing document
        const { error } = await supabase
          .from('tailored_documents')
          .update({
            content: content,
            updated_at: new Date().toISOString()
          })
          .eq('id', existingDocId);

        if (error) throw error;
        return existingDocId;
      } else {
        // Create new document
        const { data, error } = await supabase
          .from('tailored_documents')
          .insert({
            user_id: user.id,
            tailoring_session_id: 'TEST_SESSION',
            document_type: documentType,
            title: `Test ${documentType}`,
            content: content,
            job_title: 'Test Job',
            company_name: 'Test Company'
          })
          .select()
          .single();

        if (error) throw error;
        
        if (documentType === 'resume') {
          setResumeDocumentId(data.id);
        } else {
          setCoverLetterDocumentId(data.id);
        }
        
        return data.id;
      }
    } catch (error) {
      console.error('Error creating/updating document:', error);
      throw error;
    }
  };

  // EXACT same JSON generation as TailoredDocumentDetail
  const generateJSONForDocument = async (content: string, documentType: 'resume' | 'cover_letter') => {
    if (!content || content.trim().length === 0) {
      toast({
        title: "No Content",
        description: `Please enter ${documentType} text first.`,
        variant: "destructive",
      });
      return;
    }

    setIsGeneratingJson(documentType);

    try {
      console.log(`🔄 DEBUG: Starting JSON generation for ${documentType}`);
      
      // First create/update the document to get real document ID
      const documentId = await createOrUpdateDocument(content, documentType);
      if (!documentId) throw new Error('Failed to create document');

      // Call edge function to parse content into structured JSON - EXACT same method
      const { data, error } = await supabase.functions.invoke('parse-resume-to-json', {
        body: {
          resumeContent: content,
          documentType: documentType
        }
      });

      if (error) {
        console.error('❌ DEBUG: Edge function error:', error);
        throw error;
      }

      if (!data?.success || !data?.parsedData) {
        throw new Error(`Failed to parse ${documentType} content`);
      }

      console.log(`✅ DEBUG: Successfully generated JSON for ${documentType}:`, data.parsedData);

      // Save to resumejs table using EXACT same method
      await saveToResumeJsonTable(documentId, data.parsedData, documentType, `Test ${documentType}`);

      // Update state
      if (documentType === 'resume') {
        setResumeJsonData(data.parsedData);
      } else {
        setCoverLetterJsonData(data.parsedData);
      }

      toast({
        title: "Success",
        description: `${documentType} JSON generated and saved successfully`
      });

    } catch (error: any) {
      console.error(`❌ DEBUG: Error generating JSON for ${documentType}:`, error);
      toast({
        title: "Generation Failed",
        description: error.message || `Failed to generate JSON for ${documentType}`,
        variant: "destructive",
      });
    } finally {
      setIsGeneratingJson(null);
    }
  };

  const deleteJSONData = async (documentType: 'resume' | 'cover_letter') => {
    const documentId = documentType === 'resume' ? resumeDocumentId : coverLetterDocumentId;
    if (!documentId) return;

    try {
      setLoading(true);
      
      // Delete from resumejs table
      const { error: jsonError } = await supabase
        .from('resumejs')
        .delete()
        .eq('document_id', documentId);

      if (jsonError) throw jsonError;

      // Delete the document itself
      const { error: docError } = await supabase
        .from('tailored_documents')
        .delete()
        .eq('id', documentId);

      if (docError) throw docError;

      // Clear state
      if (documentType === 'resume') {
        setResumeJsonData(null);
        setResumeDocumentId(null);
        setResumeText('');
      } else {
        setCoverLetterJsonData(null);
        setCoverLetterDocumentId(null);
        setCoverLetterText('');
      }

      toast({
        title: "Success",
        description: `${documentType} and JSON data deleted successfully`
      });
      console.log(`Deleted ${documentType} and JSON data`);
    } catch (error) {
      console.error('Error deleting data:', error);
      toast({
        title: "Error",
        description: "Failed to delete data"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container mx-auto p-6">
      <Card className="max-w-6xl mx-auto">
        <CardHeader>
          <CardTitle>JSON Persistence Test - EXACT Same Method as Tailored Documents</CardTitle>
          <div className="flex gap-4 mb-4">
            <Button 
              onClick={() => navigate('/')}
              variant="outline"
            >
              Go to Home
            </Button>
            <Button 
              onClick={() => navigate('/resume-builder')}
              variant="outline"
            >
              Go to Resume Builder
            </Button>
            <Button 
              onClick={() => navigate('/tailored-document/8d568d1b-8879-4cf4-a84a-7009f149d864')}
              variant="outline"
            >
              Go to Tailored Document
            </Button>
          </div>
        </CardHeader>
        
        <CardContent>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Resume Section */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Resume Text & JSON Generation</h3>
              
              <div className="space-y-2">
                <Label htmlFor="resume-text">Paste Resume Text</Label>
                <Textarea
                  id="resume-text"
                  placeholder="Paste your resume text here..."
                  value={resumeText}
                  onChange={(e) => setResumeText(e.target.value)}
                  rows={10}
                />
              </div>
              
              {resumeText && (
                <div className="space-y-2">
                  <div className="flex gap-2">
                    <Button 
                      onClick={() => generateJSONForDocument(resumeText, 'resume')}
                      disabled={isGeneratingJson === 'resume'}
                      variant="default"
                    >
                      {isGeneratingJson === 'resume' ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Generating JSON...
                        </>
                      ) : (
                        'Generate Resume JSON'
                      )}
                    </Button>
                    
                    <Button 
                      onClick={() => deleteJSONData('resume')}
                      disabled={loading || !resumeDocumentId}
                      variant="destructive"
                      size="sm"
                    >
                      Delete All Data
                    </Button>
                  </div>
                  
                  <div className="bg-muted p-3 rounded text-sm">
                    Resume text entered ({resumeText.length} characters)
                    {resumeDocumentId && <div>Document ID: {resumeDocumentId}</div>}
                  </div>
                </div>
              )}
              
              <Tabs defaultValue="json" className="w-full mt-4">
                <TabsList>
                  <TabsTrigger value="json">Resume JSON</TabsTrigger>
                  <TabsTrigger value="formatted">Formatted</TabsTrigger>
                </TabsList>
                
                <TabsContent value="json" className="mt-4">
                  <div className="bg-muted p-4 rounded-lg max-h-96 overflow-auto">
                    <pre className="text-xs whitespace-pre-wrap">
                      {resumeJsonData ? JSON.stringify(resumeJsonData, null, 2) : 'No JSON data generated yet. Paste resume text and click "Generate Resume JSON".'}
                    </pre>
                  </div>
                </TabsContent>
                
                <TabsContent value="formatted" className="mt-4">
                  <div className="bg-muted p-4 rounded-lg max-h-96 overflow-auto">
                    {resumeJsonData ? (
                      <div className="space-y-2 text-sm">
                        {resumeJsonData.personalInfo && (
                          <div>
                            <strong>Name:</strong> {resumeJsonData.personalInfo.name}<br/>
                            <strong>Email:</strong> {resumeJsonData.personalInfo.email}<br/>
                            <strong>Phone:</strong> {resumeJsonData.personalInfo.phone}
                          </div>
                        )}
                        {resumeJsonData.skills && (
                          <div>
                            <strong>Skills:</strong> {resumeJsonData.skills.join(', ')}
                          </div>
                        )}
                      </div>
                    ) : (
                      <p className="text-muted-foreground">No data to display yet.</p>
                    )}
                  </div>
                </TabsContent>
              </Tabs>
            </div>

            {/* Cover Letter Section */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Cover Letter Text & JSON Generation</h3>
              
              <div className="space-y-2">
                <Label htmlFor="cover-letter-text">Paste Cover Letter Text</Label>
                <Textarea
                  id="cover-letter-text"
                  placeholder="Paste your cover letter text here..."
                  value={coverLetterText}
                  onChange={(e) => setCoverLetterText(e.target.value)}
                  rows={10}
                />
              </div>
              
              {coverLetterText && (
                <div className="space-y-2">
                  <div className="flex gap-2">
                    <Button 
                      onClick={() => generateJSONForDocument(coverLetterText, 'cover_letter')}
                      disabled={isGeneratingJson === 'cover_letter'}
                      variant="default"
                    >
                      {isGeneratingJson === 'cover_letter' ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Generating JSON...
                        </>
                      ) : (
                        'Generate Cover Letter JSON'
                      )}
                    </Button>
                    
                    <Button 
                      onClick={() => deleteJSONData('cover_letter')}
                      disabled={loading || !coverLetterDocumentId}
                      variant="destructive"
                      size="sm"
                    >
                      Delete All Data
                    </Button>
                  </div>
                  
                  <div className="bg-muted p-3 rounded text-sm">
                    Cover letter text entered ({coverLetterText.length} characters)
                    {coverLetterDocumentId && <div>Document ID: {coverLetterDocumentId}</div>}
                  </div>
                </div>
              )}
              
              <Tabs defaultValue="json" className="w-full mt-4">
                <TabsList>
                  <TabsTrigger value="json">Cover Letter JSON</TabsTrigger>
                  <TabsTrigger value="formatted">Formatted</TabsTrigger>
                </TabsList>
                
                <TabsContent value="json" className="mt-4">
                  <div className="bg-muted p-4 rounded-lg max-h-96 overflow-auto">
                    <pre className="text-xs whitespace-pre-wrap">
                      {coverLetterJsonData ? JSON.stringify(coverLetterJsonData, null, 2) : 'No JSON data generated yet. Paste cover letter text and click "Generate Cover Letter JSON".'}
                    </pre>
                  </div>
                </TabsContent>
                
                <TabsContent value="formatted" className="mt-4">
                  <div className="bg-muted p-4 rounded-lg max-h-96 overflow-auto">
                    {coverLetterJsonData ? (
                      <div className="space-y-2 text-sm">
                        {coverLetterJsonData.personalInfo && (
                          <div>
                            <strong>Name:</strong> {coverLetterJsonData.personalInfo.name}<br/>
                            <strong>Email:</strong> {coverLetterJsonData.personalInfo.email}
                          </div>
                        )}
                        {coverLetterJsonData.content && (
                          <div>
                            <strong>Content:</strong> {coverLetterJsonData.content.substring(0, 200)}...
                          </div>
                        )}
                      </div>
                    ) : (
                      <p className="text-muted-foreground">No data to display yet.</p>
                    )}
                  </div>
                </TabsContent>
              </Tabs>
            </div>
          </div>
          
          <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm font-medium text-blue-800 mb-2">Test Instructions - NOW EXACTLY LIKE TAILORED DOCUMENTS:</p>
            <ol className="list-decimal list-inside space-y-1 text-sm text-blue-700">
              <li>Paste resume and/or cover letter text</li>
              <li>Click "Generate JSON" - creates real documents in tailored_documents table</li>
              <li>Uses EXACT same parse-resume-to-json edge function</li>
              <li>Saves to resumejs table using real document IDs (identical to tailored page)</li>
              <li>Navigate away and return - JSON should persist exactly the same</li>
              <li>Test logout/login persistence</li>
              <li>Only "Delete All Data" removes both document and JSON</li>
            </ol>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default JSONPersistenceTest;